import React, { useState, useEffect } from 'react';
import { useCreateAssetType, useUpdateAssetType } from '../../hooks/useAssetTypes';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import type { AssetType, AssetFieldInput, FieldType } from '../../types/asset';

interface AssetTypeFormProps {
  assetType: AssetType | null;
  open: boolean;
  onClose: () => void;
}

const AssetTypeForm: React.FC<AssetTypeFormProps> = ({ assetType, open, onClose }) => {
  const isEditing = !!assetType;
  const createAssetType = useCreateAssetType();
  const updateAssetType = useUpdateAssetType();

  const [formData, setFormData] = useState({
    name: assetType?.name || '',
    nameSingular: assetType?.nameSingular || '',
    fields: assetType?.fields || [
      {
        fieldId: 'field_1',
        fieldName: '',
        fieldType: 'text' as FieldType,
        required: false,
        isPrimaryDisplay: false,
      },
    ],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (assetType) {
      setFormData({
        name: assetType.name,
        nameSingular: assetType.nameSingular,
        fields: assetType.fields,
      });
    }
  }, [assetType]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre plural es requerido';
    }

    if (!formData.nameSingular.trim()) {
      newErrors.nameSingular = 'El nombre singular es requerido';
    }

    if (formData.fields.length === 0) {
      newErrors.fields = 'Debe agregar al menos un campo';
    }

    formData.fields.forEach((field, index) => {
      if (!field.fieldName.trim()) {
        newErrors[`field_${index}_name`] = 'El nombre del campo es requerido';
      }
    });

    const primaryDisplayFields = formData.fields.filter(f => f.isPrimaryDisplay);
    if (primaryDisplayFields.length === 0) {
      newErrors.primaryDisplay = 'Debe marcar al menos un campo como principal';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Clean up field IDs: remove temporary IDs (those starting with "temp_")
    const cleanedFields = formData.fields.map(field => {
      if (field.fieldId?.startsWith('temp_')) {
        // Remove fieldId for new fields
        const { fieldId, ...fieldWithoutId } = field;
        return fieldWithoutId;
      }
      return field;
    });

    const dataToSubmit = {
      ...formData,
      fields: cleanedFields,
    };

    if (isEditing) {
      updateAssetType.mutate(
        {
          assetTypeId: assetType.assetTypeId,
          data: dataToSubmit,
        },
        {
          onSuccess: () => {
            onClose();
          },
          onError: () => {
            onClose();
          },
        }
      );
    } else {
      createAssetType.mutate(dataToSubmit, {
        onSuccess: () => {
          onClose();
        },
        onError: () => {
          onClose();
        },
      });
    }
  };

  const addField = () => {
    // Generate a temporary ID for UI purposes only (will be removed before sending to backend)
    const tempId = `temp_${Date.now()}`;
    setFormData({
      ...formData,
      fields: [
        ...formData.fields,
        {
          fieldId: tempId,
          fieldName: '',
          fieldType: 'text',
          required: false,
          isPrimaryDisplay: false,
        },
      ],
    });
  };

  const removeField = (index: number) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    setFormData({ ...formData, fields: newFields });
  };

  const updateField = (index: number, updates: Partial<AssetFieldInput>) => {
    const newFields = [...formData.fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFormData({ ...formData, fields: newFields });
  };

  const isPending = createAssetType.isLoading || updateAssetType.isLoading;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Tipo de Activo' : 'Crear Tipo de Activo'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Actualiza la configuración del tipo de activo'
              : 'Define un nuevo tipo de activo con sus campos personalizados'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Names */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Nombre Plural <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="Ej: Vehículos"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="nameSingular">
                Nombre Singular <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nameSingular"
                placeholder="Ej: Vehículo"
                value={formData.nameSingular}
                onChange={(e) => setFormData({ ...formData, nameSingular: e.target.value })}
                className={errors.nameSingular ? 'border-red-500' : ''}
              />
              {errors.nameSingular && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.nameSingular}
                </p>
              )}
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Campos <span className="text-red-500">*</span></Label>
              <Button type="button" size="sm" variant="outline" onClick={addField}>
                <Plus className="h-4 w-4 mr-1" />
                Agregar Campo
              </Button>
            </div>

            {errors.fields && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.fields}
              </p>
            )}

            {errors.primaryDisplay && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.primaryDisplay}
              </p>
            )}

            <div className="space-y-3 border rounded-lg p-4">
              {formData.fields.map((field, index) => (
                <div key={field.fieldId || `field-${index}`} className="flex gap-2 items-start p-3 bg-gray-50 rounded">
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Nombre del campo"
                      value={field.fieldName}
                      onChange={(e) => updateField(index, { fieldName: e.target.value })}
                      className={errors[`field_${index}_name`] ? 'border-red-500' : ''}
                    />
                    
                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={field.fieldType}
                        onValueChange={(value) => updateField(index, { fieldType: value as FieldType })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Texto</SelectItem>
                          <SelectItem value="number">Número</SelectItem>
                          <SelectItem value="date">Fecha</SelectItem>
                        </SelectContent>
                      </Select>

                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(index, { required: e.target.checked })}
                          className="rounded"
                        />
                        Requerido
                      </label>

                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={field.isPrimaryDisplay}
                          onChange={(e) => updateField(index, { isPrimaryDisplay: e.target.checked })}
                          className="rounded"
                        />
                        Principal
                      </label>
                    </div>
                  </div>

                  {formData.fields.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeField(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500">
              Los campos marcados como "Principal" se usarán para generar el nombre de visualización del activo
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? isEditing
                  ? 'Guardando...'
                  : 'Creando...'
                : isEditing
                ? 'Guardar Cambios'
                : 'Crear Tipo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssetTypeForm;
